import type Phaser from 'phaser';

/** Palette colours (data/assets/palette.gpl). */
const WHITE = 0xf8f2ea;
const GOLD_TIP = 0xfff1a8;
const GOLD = 0xffc82e;
const ORANGE = 0xf07a1c;

const DURATION_MS = 1400;
/** The column of light: half its width at the feet, and how high it climbs. */
const COLUMN_HALF = 7;
const COLUMN_HEIGHT = 46;
const SPARKLES = 10;

/** Feet of a character on screen, read every frame so the burst follows a walker. */
export type FeetOf = () => { x: number; y: number } | undefined;

interface Sparkle {
  dx: number;
  rise: number;
  delay: number;
  big: boolean;
}

/**
 * The burst of gold when a character levels up, drawn from code like the
 * spell effects: a ring of light spreads on the ground from the feet, a
 * column of rays climbs up through the body, and sparkles float up around it
 * and twinkle out. Whole 1×1 pixels at rounded positions (CLAUDE.md rule 6),
 * in palette colours. The far half of the ring and the column's back are
 * drawn behind the body, the rest over it. Purely visual: the server already
 * decided the level.
 */
export function playLevelUpBurst(scene: Phaser.Scene, feetOf: FeetOf): void {
  const sparkles: Sparkle[] = Array.from({ length: SPARKLES }, (_, index) => ({
    dx: ((index / SPARKLES) * 2 - 1) * 11 + (Math.random() - 0.5) * 3,
    rise: 26 + Math.random() * 22,
    delay: 0.1 + Math.random() * 0.35,
    big: index % 3 === 0,
  }));
  const seed = Math.random() * 100;
  const back = scene.add.graphics();
  const front = scene.add.graphics();
  scene.tweens.addCounter({
    from: 0,
    to: 1,
    duration: DURATION_MS,
    onUpdate: (tween) => {
      back.clear();
      front.clear();
      const feet = feetOf();
      if (feet === undefined) return;
      const x = Math.round(feet.x);
      const y = Math.round(feet.y);
      back.setDepth(y - 1);
      front.setDepth(y + 1);
      const p = tween.getValue() ?? 0;
      drawRing(back, front, x, y - 2, p);
      drawColumn(back, front, x, y - 2, p, seed);
      for (const sparkle of sparkles) drawSparkle(front, x, y, sparkle, p);
    },
    onComplete: () => {
      back.destroy();
      front.destroy();
    },
  });
}

function dot(graphics: Phaser.GameObjects.Graphics, x: number, y: number, color: number): void {
  graphics.fillStyle(color, 1);
  graphics.fillRect(Math.round(x), Math.round(y), 1, 1);
}

/** Two rings chasing each other out along the ground, flattened, cooling as they spread. */
function drawRing(
  back: Phaser.GameObjects.Graphics,
  front: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  p: number,
): void {
  for (const start of [0, 0.18]) {
    const q = (p - start) / 0.5;
    if (q <= 0 || q >= 1) continue;
    const rx = 3 + q * 15;
    const ry = rx * 0.45;
    const color = q < 0.3 ? WHITE : q < 0.6 ? GOLD_TIP : q < 0.85 ? GOLD : ORANGE;
    // One dot per pixel of circumference, so the ring stays closed as it grows.
    const steps = Math.max(12, Math.round(rx * 5));
    for (let step = 0; step < steps; step += 1) {
      const angle = (step / steps) * Math.PI * 2;
      const sin = Math.sin(angle);
      dot(sin < 0 ? back : front, cx + Math.cos(angle) * rx, cy + sin * ry, color);
    }
  }
}

/**
 * The column: vertical rays shooting up from the feet, each its own length
 * and a new flicker every frame, white at the tip. It climbs over the first
 * third, holds, then thins from the edges inward and fades from the bottom.
 */
function drawColumn(
  back: Phaser.GameObjects.Graphics,
  front: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  p: number,
  seed: number,
): void {
  const climb = Math.min(1, p / 0.3);
  const thin = p < 0.6 ? 1 : 1 - (p - 0.6) / 0.4;
  const half = Math.round(COLUMN_HALF * thin);
  // The base lifts off the ground once it starts fading: the light leaves upward.
  const lift = p < 0.6 ? 0 : Math.round(((p - 0.6) / 0.4) * COLUMN_HEIGHT * 0.7);
  for (let dx = -half; dx <= half; dx += 1) {
    // Every other ray on the far side of the body, so the light wraps around it.
    const graphics = dx % 2 === 0 ? front : back;
    const edge = Math.abs(dx) / Math.max(1, COLUMN_HALF);
    // A fixed length per ray, taller in the middle, and a flicker on top.
    const wave = Math.sin(seed + dx * 1.7) * 0.5 + 0.5;
    const flicker = Math.random() * 0.25;
    const length = Math.round(
      COLUMN_HEIGHT * climb * (1 - edge * 0.55) * (0.6 + wave * 0.3 + flicker),
    );
    // Sparse at the edges: a beam of rays, not a painted rectangle.
    if (Math.abs(dx) > 2 && Math.random() < edge * 0.6) continue;
    for (let rise = lift; rise < length; rise += 1) {
      const share = rise / Math.max(1, length);
      const color = share > 0.85 ? WHITE : share > 0.5 ? GOLD_TIP : GOLD;
      // Gaps along every ray but the core, staggered from one ray to the next,
      // so the body still shows through the light and the beam shimmers.
      const gap = Math.abs(dx) > 3 ? 2 : 3;
      if (Math.abs(dx) > 1 && (rise + dx * 2 + Math.round(seed)) % gap === 0) continue;
      dot(graphics, cx + dx, cy - rise, color);
    }
  }
}

/** A sparkle floating up beside the body: a dot, then a little cross, then a dot, gone. */
function drawSparkle(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  sparkle: Sparkle,
  p: number,
): void {
  const q = (p - sparkle.delay) / (1 - sparkle.delay);
  if (q <= 0 || q >= 1) return;
  const x = cx + sparkle.dx;
  const y = cy - 4 - sparkle.rise * (1 - (1 - q) * (1 - q));
  const color = q < 0.5 ? WHITE : q < 0.8 ? GOLD_TIP : GOLD;
  dot(graphics, x, y, color);
  // The cross opens in the middle of the sparkle's life, bigger for a few of them.
  if (q > 0.2 && q < 0.75) {
    const arm = sparkle.big && q > 0.35 && q < 0.6 ? 2 : 1;
    for (let r = 1; r <= arm; r += 1) {
      const tip = r === arm ? GOLD : GOLD_TIP;
      dot(graphics, x - r, y, tip);
      dot(graphics, x + r, y, tip);
      dot(graphics, x, y - r, tip);
      dot(graphics, x, y + r, tip);
    }
  }
}
